import React from 'react';
import { Link } from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import {
    faFacebookF,
    faInstagram,
    faLinkedinIn,
    faTwitter,
    faYoutube,
    faPinterestP,
    faTiktok,
} from '@fortawesome/free-brands-svg-icons';

import logo_icon_64 from '../assets/images/somnium/somnium-logo-white.png';
import { useLocalizedData } from '../hooks/useTranslatedContent';

export default function Footer({ contactUsData, navbarData }) {
    const { i18n } = useTranslation();
    const langCode = i18n.language;

    const { getLocalized: getNavbarLocalized } = useLocalizedData(navbarData);
    const { getLocalized: getContactLocalized } = useLocalizedData(contactUsData);

    const description = getContactLocalized('description');

    const socialLinks = {
        facebook: getContactLocalized('facebook'),
        instagram: getContactLocalized('instagram'),
        linkedin: getContactLocalized('linkedin'),
        twitter: getContactLocalized('twitter'),
        youtube: getContactLocalized('youtube'),
        pinterest: getContactLocalized('pinterest'),
        tiktok: getContactLocalized('tiktok'),
        contactEmail: contactUsData?.contactEmail
    };

    return (
        <footer className="footer bg-slate-950 relative text-gray-200 dark:text-gray-200">
            <div className="container">
                <div className="grid grid-cols-12">
                    <div className="col-span-12">
                        <div className="py-[60px] px-0">
                            <div className="grid grid-cols-1">
                                <div className="text-center">
                                    <img
                                        src={logo_icon_64}
                                        style={{ width: '150px', height: '95px' }}
                                        className="block mx-auto"
                                        alt="logo"
                                    />
                                    <p
                                        className="max-w-xl mx-auto text-slate-400 mt-8"
                                        dangerouslySetInnerHTML={{ __html: description || '' }}
                                    />
                                </div>
                                <ul className="list-none footer-list text-center mt-8 flex flex-col md:flex-row md:justify-center md:gap-x-4">
                                    <li className="px-2 py-1">
                                        <Link to={`/${langCode}`} className="text-gray-300 hover:text-gray-400 duration-500 ease-in-out">
                                            {getNavbarLocalized('home')}
                                        </Link>
                                    </li>
                                    <li className="px-2 py-1">
                                        <Link to={`/${langCode}/about`} className="text-gray-300 hover:text-gray-400 duration-500 ease-in-out">
                                            {getNavbarLocalized('about')}
                                        </Link>
                                    </li>
                                    <li className="px-2 py-1">
                                        <Link to={`/${langCode}/enterprises`} className="text-gray-300 hover:text-gray-400 duration-500 ease-in-out">
                                            {getNavbarLocalized('enterprises')}
                                        </Link>
                                    </li>
                                    <li className="px-2 py-1">
                                        <Link to={`/${langCode}/contact-us`} className="text-gray-300 hover:text-gray-400 duration-500 ease-in-out">
                                            {getNavbarLocalized('contactUs')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="py-[30px] px-0 border-t border-slate-800">
                <div className="container text-center">
                    <div className="flex flex-col items-center gap-4">
                        <p className="text-gray-400 text-sm">
                            <Trans
                                i18nKey="footer.copyright"
                                values={{ year: new Date().getFullYear() }}
                                components={{
                                    1: <FontAwesomeIcon icon={faHeart} className="text-green-700" />,
                                    2: (
                                        <Link
                                            to="https://tco.am/am"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-reset text-green-700"
                                        />
                                    )
                                }}
                            />
                        </p>

                        {/* Social Icons */}
                        <ul className="list-none flex flex-wrap justify-center gap-2">
                            {socialLinks.facebook && (
                                <li>
                                    <a
                                        href={socialLinks.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="Facebook"
                                    >
                                        <FontAwesomeIcon icon={faFacebookF} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.instagram && (
                                <li>
                                    <a
                                        href={socialLinks.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="Instagram"
                                    >
                                        <FontAwesomeIcon icon={faInstagram} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.linkedin && (
                                <li>
                                    <a
                                        href={socialLinks.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="LinkedIn"
                                    >
                                        <FontAwesomeIcon icon={faLinkedinIn} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.twitter && (
                                <li>
                                    <a
                                        href={socialLinks.twitter}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="Twitter"
                                    >
                                        <FontAwesomeIcon icon={faTwitter} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.youtube && (
                                <li>
                                    <a
                                        href={socialLinks.youtube}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="YouTube"
                                    >
                                        <FontAwesomeIcon icon={faYoutube} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.pinterest && (
                                <li>
                                    <a
                                        href={socialLinks.pinterest}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="Pinterest"
                                    >
                                        <FontAwesomeIcon icon={faPinterestP} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.tiktok && (
                                <li>
                                    <a
                                        href={socialLinks.tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="TikTok"
                                    >
                                        <FontAwesomeIcon icon={faTiktok} />
                                    </a>
                                </li>
                            )}
                            {socialLinks.contactEmail && (
                                <li>
                                    <a
                                        href={`mailto:${socialLinks.contactEmail}`}
                                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-800 rounded-md hover:border-gray-500 hover:bg-black-700 transition text-gray-300"
                                        title="Email"
                                    >
                                        <FontAwesomeIcon icon={faEnvelope} />
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    );
}